import { useContext, useEffect, useRef, useState } from "react";
import { RESOURCE_NAME } from "common/authorization";
import { FEATURE_FLAG_NAME } from "common/constants";
import { formatCoordinates } from "common/utils/coordinates";
import { CanDeleteInfo } from "../../generated/graphql";
import { track } from "../../utils/tracking";
import {
  ObjectInfoContainer,
  ObjectInfoRow,
  PrivatedInfo,
} from "../AddressPanel/Header/__styles__/Header";
import { CoordinatesContainer } from "../AddressPanel/Header/__styles__/Header";
import { AuthContext } from "../Authorization/AuthContext";
import { ActionButton } from "../Common/Buttons/ActionButton";
import { Button } from "../Common/Buttons/Button";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body, Title } from "../Common/Typography";
import { LayerContext } from "../Maps/layers";
import { MapRefreshContext } from "../Maps/MapRefreshContext";
import { ButtonRow, HeaderContainer } from "./__styles__/Header";
import { useDeleteCustomMapGeometryModal } from "./DeleteCustomMapGeometryModal";
import { ShareCustomObjectButton } from "./ShareCustomObjectButton";
import { useShareCustomObjectModal } from "./ShareCustomObjectModal";

type ObjectInfoHeaderProps = {
  loading: boolean;
  objectType: string;
  objectName: string;
  customMapId: string;
  geometryId: string;
  hiddenFromPublic: boolean;
  canDelete: Maybe<CanDeleteInfo>;
  onClose: () => void;
};

const LOADING_TEXT = "Loading...";

const HeaderActions = ({
  showShareButton,
  showDeleteButton,
  hiddenFromPublic,
  objectName,
  objectType,
  showShareModal,
  showDeleteModal,
}: {
  showShareButton: boolean | undefined;
  showDeleteButton: boolean | undefined;
  hiddenFromPublic: boolean;
  objectName: string;
  objectType: string;
  showShareModal: () => void;
  showDeleteModal: () => void;
}) => {
  if (!showShareButton && !showDeleteButton) return null;

  return (
    <ButtonRow>
      {showShareButton && (
        <ShareCustomObjectButton
          disabled={hiddenFromPublic}
          onPress={() => {
            track("Share custom object button clicked", {
              geometryLabel: objectName,
              customMapName: objectType,
            });
            showShareModal();
          }}
        />
      )}
      {showDeleteButton && (
        <Button
          size="small"
          variant="outlineInverted"
          onPress={() => {
            track("Delete custom map geometry clicked", {
              geometryLabel: objectName,
              customMapName: objectType,
            });
            showDeleteModal();
          }}
          title="Delete object"
        />
      )}
    </ButtonRow>
  );
};

const Header = ({
  loading,
  objectType,
  objectName,
  customMapId,
  geometryId,
  hiddenFromPublic,
  canDelete,
  onClose,
}: ObjectInfoHeaderProps) => {
  const { markerPoint } = useContext(LayerContext);
  const { refreshMap } = useContext(MapRefreshContext);
  const { account, isFeatureEnabled, authorized } = useContext(AuthContext);
  const longitude = markerPoint?.longitude ?? null;
  const latitude = markerPoint?.latitude ?? null;
  const displayCoordinates = longitude && latitude;

  const objectInfoRef = useRef<HTMLDivElement>(null);
  const coordinatesRef = useRef<HTMLDivElement>(null);
  const [coordinatesWrapped, setCoordinatesWrapped] = useState(false);

  const showShareButton =
    account?.publicPortal.enabled &&
    isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_CUSTOM_OBJECTS);

  const [showShareModal] = useShareCustomObjectModal({
    customMapId,
    geometryId,
    objectName,
    objectType,
  });

  const hasDeletePermission = authorized({
    resource: RESOURCE_NAME.CUSTOM_MAP_GEOMETRY,
    permission: "delete",
  });

  const [showDeleteModal] = useDeleteCustomMapGeometryModal({
    geometryId,
    customMapId,
    objectType,
    objectName,
    updateMap: () => refreshMap(["customMapsManual", "savedViews"]),
    onDelete: onClose,
  });

  const showDeleteButton = hasDeletePermission && canDelete?.deletable;

  useEffect(() => {
    const checkWrap = () => {
      if (objectInfoRef.current && coordinatesRef.current) {
        setCoordinatesWrapped(
          objectInfoRef.current.offsetTop !== coordinatesRef.current.offsetTop,
        );
      }
    };

    checkWrap();
    window.addEventListener("resize", checkWrap);
    return () => window.removeEventListener("resize", checkWrap);
  }, [coordinatesRef.current, objectInfoRef.current]);

  return (
    <HeaderContainer data-panel-header>
      {loading ? (
        <div>
          <em>{LOADING_TEXT}.</em>
        </div>
      ) : (
        <>
          <ObjectInfoRow>
            <ObjectInfoContainer ref={objectInfoRef}>
              <Body
                size="small"
                type="regular"
                color="contentSecondaryInverted"
              >
                {objectType}
              </Body>
              <CoordinatesContainer ref={coordinatesRef} displayCoordinates>
                {displayCoordinates && (
                  <Body
                    size="small"
                    type="regular"
                    color="contentSecondaryInverted"
                  >
                    {!coordinatesWrapped && `• `}
                    {`Coordinates: ${formatCoordinates({
                      latitude,
                      longitude,
                    })}`}
                  </Body>
                )}
              </CoordinatesContainer>
            </ObjectInfoContainer>
            <ActionButton
              icon="x"
              onClick={onClose}
              aria-label="Close Object Information Panel"
              data-panel-close
              variant="inverted"
            />
          </ObjectInfoRow>
          <Title size="default" type="semiBold" color="contentPrimaryInverted">
            {objectName}
          </Title>
          <HeaderActions
            showShareButton={showShareButton}
            showDeleteButton={showDeleteButton}
            hiddenFromPublic={hiddenFromPublic}
            objectName={objectName}
            objectType={objectType}
            showShareModal={showShareModal}
            showDeleteModal={showDeleteModal}
          />
          {hiddenFromPublic && showShareButton && (
            <PrivatedInfo>
              <Icon
                iconName="eye-off"
                color="contentSecondaryInverted"
                size={16}
              />
              <div>Object hidden from public</div>
            </PrivatedInfo>
          )}
        </>
      )}
    </HeaderContainer>
  );
};

export default Header;
